/**
 * Product Hooks
 * =============
 * React Query hooks for product operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { productApi } from '@/services/api'
import type { ProductCreate, ProductSearchParams, ProductUpdate } from '@/types'

// Query keys
export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (params?: ProductSearchParams) => [...productKeys.lists(), params] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...productKeys.details(), id] as const,
}

/**
 * Hook to fetch paginated product list.
 */
export function useProducts(params?: ProductSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: productKeys.list(params),
    queryFn: () => productApi.list(params),
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook to fetch a single product.
 */
export function useProduct(id: string | number) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => productApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new product.
 */
export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ProductCreate) => productApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

/**
 * Hook to update a product.
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: ProductUpdate }) =>
      productApi.update(id, data),
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
      queryClient.invalidateQueries({ queryKey: productKeys.detail(id) })
      // Also invalidate by product_id if available (for when viewing by EXO-XXXX)
      if (response?.data?.product_id) {
        queryClient.invalidateQueries({ queryKey: productKeys.detail(response.data.product_id) })
      }
    },
  })
}

/**
 * Hook to delete a product.
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => productApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}
